import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Receipt,
  CreditCard,
  Building2,
  Mail,
  Clock,
  Users,
  Shield,
  Moon,
  Smartphone,
  Github,
} from 'lucide-react'

const features = [
  {
    icon: Receipt,
    title: 'Bill Tracking',
    description: 'Manage recurring bills with due dates, amounts, frequencies, and payment status tracking.',
  },
  {
    icon: CreditCard,
    title: 'Transaction Management',
    description: 'Track expenses and income with automatic categorization based on merchant patterns.',
  },
  {
    icon: Building2,
    title: 'Bank Sync',
    description: 'Automatic transaction import via SimpleFIN Bridge for real-time account updates.',
  },
  {
    icon: Mail,
    title: 'Email Parsing',
    description: 'Automatically parse bill notification emails to extract due dates, amounts, and payment links.',
  },
  {
    icon: Clock,
    title: 'Automation',
    description: 'Scheduled email checks and transaction syncs with configurable intervals.',
  },
  {
    icon: Users,
    title: 'Household Sharing',
    description: 'Share bills, transactions, and accounts with family members in a single household.',
  },
  {
    icon: Shield,
    title: 'Secure Authentication',
    description: 'JWT-based authentication with admin approval system for new registrations.',
  },
  {
    icon: Moon,
    title: 'Dark Mode',
    description: 'Full dark mode support with system preference detection for comfortable viewing.',
  },
  {
    icon: Smartphone,
    title: 'Mobile Responsive',
    description: 'Works seamlessly on desktop and mobile devices for on-the-go access.',
  },
]

const techStack = [
  { name: 'React 18', category: 'Frontend' },
  { name: 'TypeScript', category: 'Frontend' },
  { name: 'Vite', category: 'Frontend' },
  { name: 'Tailwind CSS', category: 'Frontend' },
  { name: 'Shadcn/UI', category: 'Frontend' },
  { name: 'React Query', category: 'Frontend' },
  { name: 'Node.js', category: 'Backend' },
  { name: 'Express', category: 'Backend' },
  { name: 'SQLite', category: 'Backend' },
  { name: 'Docker', category: 'Deployment' },
]

export default function About() {
  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold tracking-tight">Centsry</h1>
        <p className="text-xl text-muted-foreground italic">Pronounced "Sensory"</p>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          A personal finance and bill management application designed to help you stay on top of
          your finances with automatic bank syncing, email parsing, and household sharing.
        </p>
      </div>

      {/* Features Grid */}
      <div>
        <h2 className="text-2xl font-semibold mb-4">Features</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <Card key={feature.title}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <feature.icon className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription>{feature.description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Tech Stack */}
      <Card>
        <CardHeader>
          <CardTitle>Technology Stack</CardTitle>
          <CardDescription>Built with modern, reliable technologies</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {techStack.map((tech) => (
              <Badge key={tech.name} variant="secondary" className="text-sm">
                {tech.name}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Links */}
      <Card>
        <CardHeader>
          <CardTitle>Open Source</CardTitle>
          <CardDescription>Centsry is open source and available on GitHub</CardDescription>
        </CardHeader>
        <CardContent>
          <a
            href="https://github.com/krazyadm1/centsry"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary hover:underline"
          >
            <Github className="h-5 w-5" />
            github.com/krazyadm1/centsry
          </a>
        </CardContent>
      </Card>

      {/* Version */}
      <div className="text-center text-sm text-muted-foreground">
        <p>Version 1.0.0</p>
        <p className="mt-1">Made with care for personal finance management</p>
      </div>
    </div>
  )
}
